package dev.lingvo.site.components.studio.editor

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import dev.lingvo.shared.screenshots.geom.toParent
import dev.lingvo.shared.textDirection
import dev.lingvo.shared.screenshots.text.cssFont
import kotlinx.browser.document
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.TextArea
import org.w3c.dom.HTMLTextAreaElement

/** Inline editing uses the same caption draft, fitting and keys save path as properties. */
@Composable
internal fun InlineTextEditor(caption: CaptionSnapshot, viewport: StageViewport, width: Int, dispatch: (StudioIntent) -> Unit,
    world: dev.lingvo.shared.screenshots.render.Affine? = null, fallback: Boolean = false) {
    if (width < 1024 || !caption.editable) return
    val box = caption.layer.base.box
    val matrix = world ?: box.toParent()
    val topLeft = viewport.toScreen(dev.lingvo.shared.screenshots.doc.Point(matrix.e, matrix.f))
    val stack = if (fallback) editorFallbackFont(dev.lingvo.shared.screenshots.ScreenshotEngine.scripts.scriptFor(caption.locale, caption.draft), caption.layer.style.role) else caption.font
    val font = cssFont(stack, (caption.fit?.layout?.size ?: caption.layer.fit.maxSize) * viewport.zoom,
        caption.layer.style.weight, caption.layer.style.italic)
    LaunchedEffect(caption.keyId, caption.locale) { (document.getElementById("studio-inline-caption") as? HTMLTextAreaElement)?.focus() }
    Div { CaptionConsequence(caption) }
    TextArea(value = caption.draft, attrs = {
        id("studio-inline-caption"); attr("aria-label", caption.key); attr("lang", caption.locale); attr("dir", textDirection(caption.locale).html)
        style {
            property("position", "absolute"); property("left", "0px"); property("top", "0px")
            property("width", "${box.w * viewport.zoom}px"); property("height", "${box.h * viewport.zoom}px")
            property("transform", "matrix(${matrix.a},${matrix.b},${matrix.c},${matrix.d},${topLeft.x},${topLeft.y})"); property("transform-origin", "0 0")
            property("font", font); property("line-height", caption.layer.style.lineHeight.toString())
            property("text-align", caption.layer.style.align.name.lowercase()); property("resize", "none")
        }
        onInput { dispatch(StudioIntent.CaptionInput(it.value)) }
        onBlur { dispatch(StudioIntent.CaptionSave) }
        onKeyDown {
            if (it.isComposing) return@onKeyDown
            when {
                it.key == "Escape" -> { it.preventDefault(); it.stopPropagation(); dispatch(StudioIntent.CaptionCancel); dispatch(StudioIntent.FocusStage) }
                it.key == "Enter" && (it.ctrlKey || it.metaKey) -> { it.preventDefault(); dispatch(StudioIntent.CaptionSave); dispatch(StudioIntent.FocusStage) }
            }
        }
    })
}
